import axios from 'axios';
import history from '../history';
import { alpha2ToAlpha3T } from '@cospired/i18n-iso-languages';
import {
  API_URL,
  ARASAAC_BASE_PATH_API,
  GLOBALSYMBOLS_BASE_PATH_API,
  AZURE_VOICES_BASE_PATH_API,
  AZURE_SPEECH_SUBSCR_KEY
} from '../constants';
import { getStore } from '../store';
import { dataURLtoFile } from '../helpers';
import { logout } from '../components/Account/Login/Login.actions.js';

const BASE_URL = API_URL.replace(/\/$/, '');
export let improvePhraseAbortController;
const ACCESS_SESSION_TOKEN = 'cloudflare-access';

const getQueryParameters = (obj = {}) =>
  Object.keys(obj)
    .filter(key => obj[key] !== undefined && obj[key] !== null)
    .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(obj[k])}`)
    .join('&');

const getDevAuthHeaders = () => {
  const sub = process.env.REACT_APP_DEV_USER_SUB;
  if (!sub) {
    return {};
  }

  return {
    'x-dev-user-sub': sub,
    'x-dev-user-email': process.env.REACT_APP_DEV_USER_EMAIL || '',
    'x-dev-user-name': process.env.REACT_APP_DEV_USER_NAME || ''
  };
};

const getRequestHeaders = (extraHeaders = {}) => ({
  ...getDevAuthHeaders(),
  ...extraHeaders
});

const decorateUser = user => {
  if (!user) {
    return null;
  }

  return {
    ...user,
    provider: 'cloudflare-access',
    authToken: ACCESS_SESSION_TOKEN,
    location:
      user.country || user.country_code
        ? {
            country: user.country || null,
            countryCode: user.country_code || null
          }
        : user.location || null
  };
};

const buildBoardPayload = board => ({
  name: board.name || 'Untitled Board',
  isPublic: !!board.isPublic,
  boardJson: board
});

const buildCommunicatorPayload = communicator => ({
  communicatorJson: communicator
});

class API {
  constructor(config = {}) {
    this.axiosInstance = axios.create({
      baseURL: BASE_URL,
      withCredentials: true,
      ...config
    });

    this.axiosInstance.interceptors.response.use(
      response => response,
      error => {
        if (
          [401, 403].includes(error.response?.status) &&
          error.config?.baseURL === BASE_URL
        ) {
          getStore().dispatch(logout());
          history.push('/');
        }
        return Promise.reject(error);
      }
    );
  }

  async getLanguage() {
    return null;
  }

  async getAzureVoices() {
    const azureVoicesListPath = `${AZURE_VOICES_BASE_PATH_API}list`;
    const headers = {
      'Ocp-Apim-Subscription-Key': AZURE_SPEECH_SUBSCR_KEY
    };

    try {
      const { status, data } = await this.axiosInstance.get(
        azureVoicesListPath,
        { headers }
      );
      if (status === 200) return data;
      return [];
    } catch (err) {
      console.error(err.message);
      return [];
    }
  }

  async arasaacPictogramsSearch(locale, searchText) {
    const path = `${ARASAAC_BASE_PATH_API}pictograms/${locale}/search/${searchText}`;
    try {
      const { status, data } = await this.axiosInstance.get(path);
      return status === 200 ? data : [];
    } catch {
      return [];
    }
  }

  async arasaacPictogramsGetImageUrl(path) {
    try {
      const { status, data } = await this.axiosInstance.get(path);
      return status === 200 ? data.image : '';
    } catch {
      return '';
    }
  }

  async globalsymbolsPictogramsSearch(locale, searchText) {
    let language = 'eng';
    if (locale.length === 3) language = locale;
    if (locale.length === 2) language = alpha2ToAlpha3T(locale);

    const path = `${GLOBALSYMBOLS_BASE_PATH_API}labels/search/?query=${searchText}&language=${language}&language_iso_format=639-3&limit=20`;
    try {
      const { status, data } = await this.axiosInstance.get(path);
      return status === 200 ? data : [];
    } catch {
      return [];
    }
  }

  async login() {
    throw new Error('Local login has been removed. Use Cloudflare Access.');
  }

  async forgot() {
    throw new Error('Password reset flow has been removed.');
  }

  async storePassword() {
    throw new Error('Password storage flow has been removed.');
  }

  async oAuthLogin() {
    throw new Error('OAuth callback flow has been removed.');
  }

  async getUserData() {
    const { data } = await this.axiosInstance.get('/api/me', {
      headers: getRequestHeaders()
    });
    return decorateUser(data.user);
  }

  async getBoards(options = {}) {
    return this.getMyBoards(options);
  }

  async getPublicBoards({
    page = 1,
    limit = 10,
    search = ''
  } = {}) {
    const query = getQueryParameters({
      scope: 'public',
      page,
      limit,
      search
    });
    const { data } = await this.axiosInstance.get(`/api/boards?${query}`, {
      headers: getRequestHeaders()
    });
    return data;
  }

  async getMyBoards({
    page = 1,
    limit = 10,
    search = ''
  } = {}) {
    const query = getQueryParameters({
      scope: 'mine',
      page,
      limit,
      search
    });
    const { data } = await this.axiosInstance.get(`/api/boards?${query}`, {
      headers: getRequestHeaders()
    });
    return data;
  }

  async getCommunicators({
    page = 1,
    limit = 10
  } = {}) {
    const query = getQueryParameters({ page, limit });
    const { data } = await this.axiosInstance.get(
      `/api/communicators?${query}`,
      {
        headers: getRequestHeaders()
      }
    );
    return data;
  }

  async getBoard(id) {
    const { data } = await this.axiosInstance.get(`/api/boards/${id}`, {
      headers: getRequestHeaders()
    });
    return data.board;
  }

  async getCbuilderBoard(id) {
    return this.getBoard(id);
  }

  async getSettings() {
    const { data } = await this.axiosInstance.get('/api/settings', {
      headers: getRequestHeaders()
    });
    return data;
  }

  async updateSettings(newSettings = {}) {
    const { data } = await this.axiosInstance.post('/api/settings', newSettings, {
      headers: getRequestHeaders()
    });
    return data;
  }

  async updateUser(user) {
    const { data } = await this.axiosInstance.put(
      '/api/me',
      {
        name: user.name,
        birthdate: user.birthdate,
        country: user.location?.country || user.country,
        countryCode: user.location?.countryCode || user.countryCode
      },
      {
        headers: getRequestHeaders()
      }
    );
    return decorateUser(data.user);
  }

  async createBoard(board) {
    const { data } = await this.axiosInstance.post(
      '/api/boards',
      buildBoardPayload(board),
      {
        headers: getRequestHeaders()
      }
    );
    return data.board;
  }

  async updateBoard(board) {
    const { data } = await this.axiosInstance.put(
      `/api/boards/${board.id}`,
      buildBoardPayload(board),
      {
        headers: getRequestHeaders()
      }
    );
    return data.board;
  }

  async deleteBoard(boardId) {
    const { data } = await this.axiosInstance.delete(`/api/boards/${boardId}`, {
      headers: getRequestHeaders()
    });
    return data;
  }

  async boardReport() {
    return { success: true };
  }

  async uploadFromDataURL(dataURL, filename, checkExtension = false) {
    const file = dataURLtoFile(dataURL, filename, checkExtension);
    return this.uploadFile(file, filename);
  }

  async uploadFile(file) {
    const { data } = await this.axiosInstance.post(
      '/api/media/upload-url',
      {},
      {
        headers: getRequestHeaders()
      }
    );

    await this.axiosInstance.put(data.uploadUrl, file, {
      headers: getRequestHeaders({
        'Content-Type': file.type || 'application/octet-stream'
      })
    });

    return data.publicUrl;
  }

  async createCommunicator(communicator) {
    const communicatorId = communicator.id || 'cboard_default';
    const { data } = await this.axiosInstance.put(
      `/api/communicators/${communicatorId}`,
      buildCommunicatorPayload(communicator),
      {
        headers: getRequestHeaders()
      }
    );
    return data.communicator;
  }

  async updateCommunicator(communicator) {
    const communicatorId = communicator.id || 'cboard_default';
    const { data } = await this.axiosInstance.put(
      `/api/communicators/${communicatorId}`,
      buildCommunicatorPayload(communicator),
      {
        headers: getRequestHeaders()
      }
    );
    return data.communicator;
  }

  async analyticsReport() {
    return {};
  }

  async getUserLocation() {
    const { data } = await this.axiosInstance.get('/api/location', {
      headers: getRequestHeaders()
    });
    return data;
  }

  async getSubscriber() {
    throw new Error('Subscriptions have been removed.');
  }

  async createSubscriber() {
    throw new Error('Subscriptions have been removed.');
  }

  async cancelPlan() {
    throw new Error('Subscriptions have been removed.');
  }

  async postTransaction() {
    throw new Error('Subscriptions have been removed.');
  }

  async updateSubscriber() {
    throw new Error('Subscriptions have been removed.');
  }

  async listSubscriptions() {
    return [];
  }

  async deleteAccount() {
    const { data } = await this.axiosInstance.delete('/api/me', {
      headers: getRequestHeaders()
    });
    return data;
  }

  async improvePhrase({ phrase, language }) {
    try {
      improvePhraseAbortController = new AbortController();
      const { data } = await this.axiosInstance.post(
        '/api/ai/improve-phrase',
        { phrase, language },
        {
          headers: getRequestHeaders(),
          signal: improvePhraseAbortController.signal
        }
      );
      return data;
    } catch (error) {
      if (error.message !== 'canceled') console.error(error);
      return { phrase: '' };
    }
  }
}

const API_INSTANCE = new API({});

export default API_INSTANCE;
